'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface FilterPanelProps {
  onFilterChange: (filters: FilterState) => void;
  resultCount: number;
}

export interface FilterState {
  categories: string[];
  priceRange: [number, number];
  sizes: string[];
  availability: string[];
  sortBy: string;
}

const FilterPanel = ({ onFilterChange, resultCount }: FilterPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [filters, setFilters] = useState<FilterState>({
    categories: [],
    priceRange: [0, 5000],
    sizes: [],
    availability: [],
    sortBy: 'relevance',
  });

  const categories: FilterOption[] = [
    { id: 'fresh-fish', label: 'Fresh Fish', count: 45 },
    { id: 'fish-cuts', label: 'Fish Cuts', count: 32 },
    { id: 'processed', label: 'Processed', count: 18 },
    { id: 'dried', label: 'Dried Fish', count: 12 },
  ];

  const sizes: FilterOption[] = [
    { id: 'small', label: 'Small (< 500g)', count: 28 },
    { id: 'medium', label: 'Medium (500g - 1kg)', count: 42 },
    { id: 'large', label: 'Large (> 1kg)', count: 37 },
  ];

  const availability: FilterOption[] = [
    { id: 'in-stock', label: 'In Stock', count: 89 },
    { id: 'low-stock', label: 'Low Stock', count: 12 },
    { id: 'pre-order', label: 'Pre-Order', count: 6 },
  ];

  const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'freshness', label: 'Freshness' },
    { value: 'popularity', label: 'Popularity' },
  ];

  const handleCategoryToggle = (categoryId: string) => {
    const newCategories = filters.categories.includes(categoryId)
      ? filters.categories.filter((c) => c !== categoryId)
      : [...filters.categories, categoryId];
    
    const newFilters = { ...filters, categories: newCategories };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSizeToggle = (sizeId: string) => {
    const newSizes = filters.sizes.includes(sizeId)
      ? filters.sizes.filter((s) => s !== sizeId)
      : [...filters.sizes, sizeId];
    
    const newFilters = { ...filters, sizes: newSizes };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleAvailabilityToggle = (availId: string) => {
    const newAvailability = filters.availability.includes(availId)
      ? filters.availability.filter((a) => a !== availId)
      : [...filters.availability, availId];
    
    const newFilters = { ...filters, availability: newAvailability };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handlePriceChange = (min: number, max: number) => {
    const newFilters = { ...filters, priceRange: [min, max] as [number, number] };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSortChange = (sortValue: string) => {
    const newFilters = { ...filters, sortBy: sortValue };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearAll = () => {
    const clearedFilters: FilterState = {
      categories: [],
      priceRange: [0, 5000],
      sizes: [],
      availability: [],
      sortBy: 'relevance',
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const activeFilterCount = 
    filters.categories.length + 
    filters.sizes.length + 
    filters.availability.length +
    (filters.priceRange[0] !== 0 || filters.priceRange[1] !== 5000 ? 1 : 0);

  return (
    <div className="bg-card border border-border rounded-md shadow-elevation-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <Icon name="FunnelIcon" size={20} className="text-primary" />
          <div>
            <h2 className="font-heading font-semibold text-lg text-foreground">Filters</h2>
            <p className="font-caption text-xs text-muted-foreground">
              {resultCount} products found
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeFilterCount > 0 && (
            <button
              onClick={handleClearAll}
              className="px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 rounded-md transition-smooth"
            >
              Clear All ({activeFilterCount})
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-muted rounded-md transition-smooth lg:hidden"
            aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
          >
            <Icon 
              name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
              size={20} 
            />
          </button>
        </div>
      </div>

      {/* Filter Content */}
      <div className={`${isExpanded ? 'block' : 'hidden lg:block'}`}>
        <div className="p-4 space-y-6">
          {/* Sort By */}
          <div>
            <label className="block font-medium text-sm text-foreground mb-3">
              Sort By
            </label>
            <select
              value={filters.sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus-ring"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Categories */}
          <div>
            <h3 className="font-medium text-sm text-foreground mb-3">Category</h3>
            <div className="space-y-2">
              {categories.map((category) => (
                <label
                  key={category.id}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-muted cursor-pointer transition-smooth"
                >
                  <input
                    type="checkbox"
                    checked={filters.categories.includes(category.id)}
                    onChange={() => handleCategoryToggle(category.id)}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
                  />
                  <span className="flex-1 text-sm text-foreground">{category.label}</span>
                  <span className="font-data text-xs text-muted-foreground">
                    {category.count}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Price Range */}
          <div>
            <h3 className="font-medium text-sm text-foreground mb-3">Price Range</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  value={filters.priceRange[0]}
                  onChange={(e) => handlePriceChange(Number(e.target.value), filters.priceRange[1])}
                  placeholder="Min"
                  className="flex-1 px-3 py-2 bg-background border border-input rounded-md text-sm focus-ring"
                />
                <span className="text-muted-foreground">-</span>
                <input
                  type="number"
                  value={filters.priceRange[1]}
                  onChange={(e) => handlePriceChange(filters.priceRange[0], Number(e.target.value))}
                  placeholder="Max"
                  className="flex-1 px-3 py-2 bg-background border border-input rounded-md text-sm focus-ring"
                />
              </div>
              <input
                type="range"
                min="0"
                max="5000"
                step="100"
                value={filters.priceRange[1]}
                onChange={(e) => handlePriceChange(filters.priceRange[0], Number(e.target.value))}
                className="w-full accent-primary"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>₹0</span>
                <span>₹5,000</span>
              </div>
            </div>
          </div>

          {/* Size */}
          <div>
            <h3 className="font-medium text-sm text-foreground mb-3">Size</h3>
            <div className="space-y-2">
              {sizes.map((size) => (
                <label
                  key={size.id}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-muted cursor-pointer transition-smooth"
                >
                  <input
                    type="checkbox"
                    checked={filters.sizes.includes(size.id)}
                    onChange={() => handleSizeToggle(size.id)}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
                  />
                  <span className="flex-1 text-sm text-foreground">{size.label}</span>
                  <span className="font-data text-xs text-muted-foreground">
                    {size.count}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Availability */}
          <div>
            <h3 className="font-medium text-sm text-foreground mb-3">Availability</h3>
            <div className="space-y-2">
              {availability.map((avail) => (
                <label
                  key={avail.id}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-muted cursor-pointer transition-smooth"
                >
                  <input
                    type="checkbox"
                    checked={filters.availability.includes(avail.id)}
                    onChange={() => handleAvailabilityToggle(avail.id)}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
                  />
                  <span className="flex-1 text-sm text-foreground">{avail.label}</span>
                  <span className="font-data text-xs text-muted-foreground">
                    {avail.count}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;